
import React, { useState, useEffect } from 'react';
import { isNativePlatform } from '@/services/platformService';
import petiusLogo from "@/assets/petius-logo.png";
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { getUserRole } from '@/services/moderationService';
import { useFeedbackNotifications } from '@/hooks/useFeedbackNotifications';
import { useModerationNotifications } from '@/hooks/useModerationNotifications';
import { useIsProvider } from '@/hooks/useIsProvider';
import {
  User,
  Settings,
  LogOut,
  CreditCard,
  Bell,
  Shield,
  ShieldCheck,
  Users,
  Mail,
  MessageSquare,
  PawPrint,
  Heart,
  Inbox,
  LayoutDashboard
} from 'lucide-react';

const UserMenu = () => {
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const [userRole, setUserRole] = useState<string | null>(null);
  const { data: isProvider } = useIsProvider();

  useEffect(() => {
    const checkUserRole = async () => {
      if (user) {
        const role = await getUserRole();
        setUserRole(role);
      }
    };
    checkUserRole();
  }, [user]);

  const { pendingCount } = useFeedbackNotifications(userRole);
  const { pendingCount: pendingReportsCount } = useModerationNotifications(userRole);

  if (!user) return null;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const displayName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-10 w-10 rounded-full">
          <Avatar className="h-10 w-10">
            <AvatarImage
              src={user.user_metadata?.avatar_url}
              alt={displayName}
            />
            <AvatarFallback>
              {getInitials(displayName)}
            </AvatarFallback>
          </Avatar>
          {userRole && (
            <Badge
              variant={userRole === 'admin' ? 'destructive' : 'secondary'}
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {userRole === 'admin' ? 'A' : 'M'}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <div className="flex items-center gap-2 mb-2">
              <img src={petiusLogo} alt="Petius" className="w-5 h-5 rounded-full object-cover" />
              <span className="text-sm petius-brand">Petius</span>
            </div>
            <p className="text-sm font-medium leading-none">
              {displayName}
              {userRole && (
                <Badge
                  variant={userRole === 'admin' ? 'destructive' : 'secondary'}
                  className="ml-2 text-xs"
                >
                  {userRole === 'admin' ? 'Admin' : 'Moderator'}
                </Badge>
              )}
            </p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {isProvider && (
          <DropdownMenuItem asChild>
            <Link to="/provider-dashboard" className="cursor-pointer">
              <LayoutDashboard className="mr-2 h-4 w-4 text-pet-purple" />
              <span>{t('navigation.providerDashboard')}</span>
            </Link>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem asChild>
          <Link to="/profile" className="cursor-pointer">
            <User className="mr-2 h-4 w-4 text-pet-blue" />
            <span>{t('navigation.profile')}</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link to="/matches/discover" className="cursor-pointer">
            <Heart className="mr-2 h-4 w-4 text-red-500" />
            <span>{t('navigation.discoverMatches')}</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link to="/matches" className="cursor-pointer">
            <Inbox className="mr-2 h-4 w-4 text-pet-purple" />
            <span>{t('navigation.matchInbox')}</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link to="/notifications" className="cursor-pointer">
            <Bell className="mr-2 h-4 w-4 text-pet-orange" />
            <span>{t('navigation.notifications')}</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link to="/settings" className="cursor-pointer">
            <Settings className="mr-2 h-4 w-4 text-muted-foreground" />
            <span>{t('navigation.settings')}</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link to="/subscription" className="cursor-pointer">
            <CreditCard className="mr-2 h-4 w-4 text-pet-yellow" />
            <span>{t('navigation.subscription')}</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/contact" className="cursor-pointer">
            <Mail className="mr-2 h-4 w-4 text-blue-500" />
            <span>{t('navigation.contact')}</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link to="/feedback" className="cursor-pointer">
            <MessageSquare className="mr-2 h-4 w-4 text-pet-purple" />
            <span>{t('navigation.feedback')}</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          {isNativePlatform() ? (
            <a href="https://petius.io/donate" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <Heart className="mr-2 h-4 w-4 text-red-500 fill-red-500" />
              <span className="font-semibold text-red-600">{t('navigation.supportUs')} ❤️</span>
            </a>
          ) : (
            <Link to="/donate" className="cursor-pointer">
              <Heart className="mr-2 h-4 w-4 text-red-500 fill-red-500" />
              <span className="font-semibold text-red-600">{t('navigation.supportUs')} ❤️</span>
            </Link>
          )}
        </DropdownMenuItem>

        {userRole && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/moderation" className="cursor-pointer">
                <Shield className="mr-2 h-4 w-4 text-pet-orange" />
                <span>{t('navigation.moderationPanel')}</span>
                {pendingReportsCount > 0 && (
                  <Badge variant="destructive" className="ml-auto h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {pendingReportsCount}
                  </Badge>
                )}
              </Link>
            </DropdownMenuItem>

            {userRole === 'admin' && (
              <>
                <DropdownMenuItem asChild>
                  <Link to="/feedback-management" className="cursor-pointer">
                    <MessageSquare className="mr-2 h-4 w-4 text-green-500" />
                    <span>{t('navigation.feedbackManagement')}</span>
                    {pendingCount > 0 && (
                      <Badge variant="destructive" className="ml-auto h-5 w-5 p-0 flex items-center justify-center text-xs">
                        {pendingCount}
                      </Badge>
                    )}
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link to="/admin" className="cursor-pointer">
                    <ShieldCheck className="mr-2 h-4 w-4 text-red-600" />
                    <span>{t('navigation.adminManagement')}</span>
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                  <Link to="/admin/user-management" className="cursor-pointer">
                    <Users className="mr-2 h-4 w-4 text-blue-600" />
                    <span>{t('navigation.userManagement')}</span>
                  </Link>
                </DropdownMenuItem>
              </>
            )}
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer text-red-600 focus:text-red-600"
          onClick={() => signOut()}
        >
          <LogOut className="mr-2 h-4 w-4 text-red-600" />
          <span>{t('navigation.logOut')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
